import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import STATIC_BRAND from "@/config/brand";

/**
 * BrandContext — merges the static `brand.js` defaults with any admin-editable
 * overrides stored in MongoDB (`brand_settings.singleton`). Components that
 * want live-updating brand copy call `useBrand()`; anything using the static
 * `import BRAND from "@/config/brand"` still works as a hard-coded fallback.
 */
const BrandContext = createContext({ brand: STATIC_BRAND, refresh: () => {} });

// Flatten server response -> nested shape matching brand.js
function shapeFromServer(server) {
  return {
    ...STATIC_BRAND,
    tagline: server.tagline || STATIC_BRAND.tagline,
    motto: server.motto || STATIC_BRAND.motto,
    description: server.description || STATIC_BRAND.description,
    supportEmail: server.support_email || STATIC_BRAND.supportEmail,
    poweredBy: {
      name: server.powered_by_name || STATIC_BRAND.poweredBy.name,
      url: server.powered_by_url || STATIC_BRAND.poweredBy.url,
    },
    social: {
      facebook: server.social_facebook || "",
      instagram: server.social_instagram || "",
      twitter: server.social_twitter || "",
      tiktok: server.social_tiktok || "",
      youtube: server.social_youtube || "",
    },
    campaign: {
      ...STATIC_BRAND.campaign,
      displayName: server.campaign_display_name || STATIC_BRAND.campaign.displayName,
      grandPrize: {
        ...STATIC_BRAND.campaign.grandPrize,
        shortName: server.grand_prize_short || STATIC_BRAND.campaign.grandPrize.shortName,
        description: server.grand_prize_description || STATIC_BRAND.campaign.grandPrize.description,
        arv: server.grand_prize_arv || STATIC_BRAND.campaign.grandPrize.arv,
      },
    },
  };
}

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(STATIC_BRAND);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/brand");
      setBrand(shapeFromServer(data));
    } catch {
      // Fall back to static defaults on error — never crash the app.
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Preview mode: when this app is loaded inside the Brand-Manager iframe
  // (URL contains ?preview=1), listen for postMessage draft-brand updates
  // from the parent window and apply them WITHOUT hitting the API.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("preview") !== "1") return;
    const handler = (ev) => {
      if (ev.origin !== window.location.origin) return;
      if (!ev.data || ev.data.type !== "brand-preview" || !ev.data.data) return;
      try {
        setBrand(shapeFromServer(ev.data.data));
      } catch {
        /* noop */
      }
    };
    window.addEventListener("message", handler);
    // Announce we're ready so the parent can push its initial draft.
    try {
      window.parent?.postMessage({ type: "brand-preview-ready" }, window.location.origin);
    } catch { /* noop */ }
    return () => window.removeEventListener("message", handler);
  }, []);

  return (
    <BrandContext.Provider value={{ brand, refresh }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandContext);
}
